import React from 'react';
import classnames from 'classnames';
import { getInitials, toTitleCase } from 'helpers/string';
import { CollaboratorAvatarSize, CollaboratorAvatarType } from 'helpers/constants';

import styles from './CollaboratorAvatar.module.scss';
import { Emoji } from 'emoji-mart';

interface CollaboratorAvatarProps {
  /** @Collaborator - Collaborator data */
  collaborator: Collaborator;
  /** @string - Classname to be passed to the wrapper element */
  className?: string;
  /** @boolean - If the avatar should have a border */
  bordered?: boolean;
  /** @string - Displayed name if initials cannot be found */
  defaultName?: string;
  /** @CollaboratorAvatarSize - Collaborator avatar size variation */
  size?: CollaboratorAvatarSize;
  /** @boolean - If the collaborator avatar should inherit width and height from the parent */
  inheritSize?: boolean;
  /** @CollaboratorAvatarType - Type of collaborator */
  type?: CollaboratorAvatarType;
  /** @string - Classname for the hover effects */
  hoverEffects?: string;
  /** @string - The emoji to render. Used for groups */
  emoji?: string;
}

const CollaboratorAvatar: React.FC<CollaboratorAvatarProps> = ({
  size,
  type = 'Person',
  bordered = true,
  defaultName = '?',
  emoji = '',
  inheritSize,
  collaborator: { displayName, emailAddress, photoURL },
  className,
  hoverEffects,
}) => {
  const [isBrokenImageUrl, setBrokenImageUrl] = React.useState(false);

  const classes = classnames(className, {
    [styles.container]: true,
    [styles[`size${size}`]]: size,
    [styles[`type-${type}`]]: type,
    [styles[`hoverEffects-${hoverEffects}`]]: hoverEffects,
  });

  const group = type === 'Group';
  const initials = getInitials(displayName!, emailAddress, group ? 1 : 2);

  /**
   * Returns the emoji size for each specific size preset
   */
  const getEmojiSize = (): number => {
    switch (size) {
      case 'Small':
        return 16;
      case 'Medium':
        return 20;
      case 'SubStandard':
        return 24;
      case 'Large':
        return 26;
      case 'XL':
        return 54;
      case 'Enormous':
        return 80;
      default:
        return 16;
    }
  };

  /**
   * If the collaborator is a group, renders either
   * the emoji or the default group avatar
   */
  const renderGroupAvatar = () => {
    if (emoji) {
      return (
        <div className={styles.initials} title={toTitleCase(displayName!)}>
          <Emoji
            native={false}
            emoji={emoji}
            size={getEmojiSize()}
            fallback={renderGroupInitials as any}
          />
        </div>
      );
    } else {
      return renderGroupInitials();
    }
  };

  /**
   * Renders the group initials
   */
  const renderGroupInitials = () => {
    return (
      <div className={styles.initials} title={toTitleCase(displayName!)}>
        <div className={styles.initials__text}>{initials}</div>
      </div>
    );
  };

  /**
   * If the collaborator is a person and has an image, render the image
   * If the collaborator does not have an image show the display name initials
   */
  const renderPersonAvatar = () => {
    if (!isBrokenImageUrl && photoURL) {
      return (
        <img
          src={photoURL}
          alt={displayName || emailAddress}
          className={styles.image}
          title={toTitleCase(displayName!) || emailAddress}
          onError={() => setBrokenImageUrl(true)}
        />
      );
    } else {
      return (
        <div className={styles.initials} title={toTitleCase(displayName!) || emailAddress}>
          <div className={styles.initials__text}>{initials || defaultName}</div>
        </div>
      );
    }
  };

  return (
    <div className={classes}>
      <div
        className={classnames(styles.avatarInner, {
          [styles.bordered]: bordered,
          [styles.inheritSize]: inheritSize,
        })}
      >
        {group ? renderGroupAvatar() : renderPersonAvatar()}
      </div>
    </div>
  );
};

export default CollaboratorAvatar;
